import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { NodeAPI, FlaskAPI } from "../api";
import { useAuth } from "../context/AuthContext";
import { jsPDF } from "jspdf";
import Tesseract from "tesseract.js"; // OCR library
import { Link } from "react-router-dom";
import CircularProgress from '@mui/material/CircularProgress';
import { storePendingQuiz } from "../utils/pendingQuiz";
import QuizGeneratorMeta from "../components/QuizGeneratorMeta";


const QuizGenerator = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [paragraph, setParagraph] = useState("");
    const [category, setCategory] = useState("multiple_choice");
    const [generatedQuestions, setGeneratedQuestions] = useState([]);
    const [topic, setTopic] = useState("");
    const [loading, setLoading] = useState(false);
    const [language, setLanguage] = useState("eng");
    const [recording, setRecording] = useState(false);

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setLoading(true);
        try {
            const { data } = await Tesseract.recognize(file, language);
            setParagraph(data.text);
        } catch (error) {
            console.error("OCR failed:", error);
            alert("Failed to extract text from image.");
        }
        setLoading(false);
    };

    const startRecording = () => {
        if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
            alert("Speech recognition is not supported in this browser.");
            return;
        }

        const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
        recognition.lang = language;
        recognition.continuous = false;

        recognition.onstart = () => setRecording(true);
        recognition.onend = () => setRecording(false);

        recognition.onresult = (event) => {
            const transcript = event.results[0][0].transcript;
            setParagraph((prev) => prev + " " + transcript);
        };

        recognition.start();
    };

    const generateQuiz = async () => {
        if (!paragraph) return alert("Please enter a paragraph.");
        setLoading(true);

        try {
            const response = await FlaskAPI.post('/generate_quiz', { paragraph, category });
            setGeneratedQuestions(response.data.questions || []);
        } catch (error) {
            console.error('Error generating quiz:', error);
            alert("Failed to generate quiz.");
        }

        setLoading(false);
    };

    const saveQuiz = async () => {
        if (!topic) return alert("Please enter a topic.");
        if (!generatedQuestions.length) return alert("No questions to save.");
        if (!user) {
            try {
                storePendingQuiz({ topic: topic.trim(), category, questions: generatedQuestions });
                navigate("/login");
            } catch (error) {
                alert("Could not keep your quiz for login. Please try again.");
            }
            return;
        }

        try {
            await NodeAPI.post("/quiz/save", { 
                topic, category, questions: generatedQuestions, userId: user.id 
            });
            alert("Quiz saved successfully!");
        } catch (error) {
            console.error("Error saving quiz:", error);
            alert("Failed to save quiz.");
        }
    };

  


    const generatePDF = (includeAnswers) => {
        const doc = new jsPDF();
        const quizTitle = topic?.trim() ? topic : "Untitled Quiz";
    
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.text(`Quiz: ${quizTitle}`, 10, 10);
    
        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);
    
        let yPosition = 20;
    
        generatedQuestions.forEach((q, index) => {
            if (yPosition > 270) {
                doc.addPage();
                yPosition = 20;
            }
    
            const questionLines = doc.splitTextToSize(`${index + 1}. ${q.question}`, 180);
            doc.text(questionLines, 10, yPosition);
            yPosition += questionLines.length * 6;
    
            if (q.options?.length) {
                q.options.forEach((option, i) => {
                    const optionText = `${String.fromCharCode(65 + i)}. ${option}`;
                    const wrappedOption = doc.splitTextToSize(optionText, 170);
                    doc.text(wrappedOption, 15, yPosition);
                    yPosition += wrappedOption.length * 6;
                });
            }
    
            if (includeAnswers && q.answer != null) {
                let answerText = "";
    
                if (typeof q.answer === "number" && q.options?.[q.answer]) {
                    answerText = q.options[q.answer];
                } else if (typeof q.answer === "string") {
                    answerText = q.answer;
                }
    
                const answerLines = doc.splitTextToSize(`Answer: ${answerText}`, 170);
                doc.text(answerLines, 10, yPosition);
                yPosition += answerLines.length * 6;
            }
    
            yPosition += 8;
        });
    
        doc.save(`${quizTitle}.pdf`);
    };
    
    

    const takeQuiz = () => {
        if (!generatedQuestions.length) return alert("No quiz questions available.");
        navigate("/take-quiz", { state: { questions: generatedQuestions, topic, category } });
    };
    const goToCategory = () => {
        navigate("/register"); // Replace with the actual category page route
    };

    const exitQuizGenerator = () => {
        navigate("/login"); // Replace with the actual exit page route
    };

    return (
     
      <>
      <QuizGeneratorMeta />
        <div style={styles.container}>
            <h2 style={styles.heading}>Generate Quiz from Paragraph</h2>

            <label style={styles.label}>Choose Language:</label>
            <select value={language} onChange={(e) => setLanguage(e.target.value)} style={styles.select}>
                <option value="eng">English</option>
                <option value="spa">Spanish</option>
                <option value="fra">French</option>
                <option value="deu">German</option>
                <option value="ita">Italian</option>
                <option value="por">Portuguese</option>
                <option value="zho">中文 (Chinese)</option>
                <option value="kor">한국어 (Korean)</option>
                <option value="hin">हिन्दी (Hindi)</option>
                <option value="rus">Русский (Russian)</option>
            </select>

            <input type="file" accept="image/*" onChange={handleImageUpload} style={styles.fileInput} />
            <button onClick={startRecording} style={styles.button}>
                {recording ? "🎤 Listening..." : "🎙️ Speak Now"}
            </button>

            <textarea
                value={paragraph}
                onChange={(e) => setParagraph(e.target.value)}
                placeholder="Enter or extract text..."
                style={styles.textarea}
            />

            <select value={category} onChange={(e) => setCategory(e.target.value)} style={styles.select}>
                <option value="multiple_choice">Multiple Choice</option>
                <option value="true_false">True/False</option>
                <option value="structured" >Structured</option>
              
            </select>

<p style={styles.termsText}>
    By using this service, you agree to our{" "}
    <Link to="/terms" style={styles.link}>
        Terms and Conditions
    </Link>{" "}
    and{" "}
    <Link to="/privacy" style={styles.link}>
        Privacy Policy
    </Link>.
</p>

            <button onClick={generateQuiz} disabled={loading} style={styles.button}>
                {loading ? "Generating..." : "Generate Quiz"}
            </button>



            <button onClick={goToCategory} style={styles.exitButton}>Register</button>
            <button onClick={exitQuizGenerator} style={styles.exitButton}>Login</button>
            {loading && (
  <div style={{ marginTop: "10px" }}>
    <CircularProgress />
  </div>
)}

            {generatedQuestions.length > 0 && (
                <div style={styles.quizContainer}>
                    <h3>Generated Questions</h3>
                    <ul>
                        {generatedQuestions.map((q, index) => (
                            <li key={index} style={styles.question}>
                                <strong>{q.question}</strong>
                                {q.options?.length > 0 && (
                                    <ul>
                                        {q.options.map((option, i) => (
                                            <li key={i}>{option}</li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>

                    <input 
                        value={topic} 
                        onChange={(e) => setTopic(e.target.value)} 
                        placeholder="Enter topic name"
                        style={styles.input}
                    />
                   <button onClick={() => generatePDF(true)} style={styles.button}>Download PDF (With Answers)</button>
                    <button onClick={() => generatePDF(false)} style={styles.button}>Download PDF (Without Answers)</button>
                    <button onClick={takeQuiz} style={styles.button}>Take Quiz</button>
                    <button onClick={saveQuiz} style={styles.button}>Save Quiz</button>
                </div>
            )}

        </div>
</>
    );
};

const styles = {
    container: { maxWidth: "600px", margin: "auto", padding: "20px", textAlign: "center" },
    heading: { fontSize: "24px", marginBottom: "10px" },
    label: { fontWeight: "bold" },
    select: { width: "100%", padding: "8px", marginBottom: "10px" },
    fileInput: { marginBottom: "10px" },
    textarea: { width: "100%", height: "100px", padding: "10px", marginBottom: "10px" },
    button: { padding: "10px", margin: "5px", cursor: "pointer", backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "5px" },
    quizContainer: { textAlign: "left", marginTop: "20px" },
    question: { marginBottom: "10px" },
    input: { width: "100%", padding: "8px", marginBottom: "10px" },
    exitButton: { padding: "10px", margin: "5px", cursor: "pointer", backgroundColor: "#dc3545", color: "white", border: "none", borderRadius: "5px" },
termsText: {
    fontSize: "14px",
    marginTop: "20px",
    color: "#555"
},
link: {
    color: "#007bff",
    textDecoration: "underline",
    cursor: "pointer"
}



};

export default QuizGenerator;